import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './DocumentUploadModal.module.scss';
import Modal from '../modal/Modal';
import { useDropzone } from 'react-dropzone';
import Icon from '../icon/Icon';
import Dropdown from '../dropdown/Dropdown';
import CustomDatePicker from '../customDatePicker/CustomDatePicker';

import axiosClient from '../../api/axiosClient';
import { useToasts } from '../../contexts/ToastContext';

const DocumentUploadModal = ({ isOpen, onClose, siteId, onUploadSuccess, endpointPrefix = 'clinical-sites' }) => {
  const [files, setFiles] = useState([]);
  const { showToast } = useToasts();

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      type: 'Agreement', // Default type
      activationDate: null,
      expirationDate: null,
      status: 'Ready to upload',
      errors: {}
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const handleUpdateFile = (id, field, value) => {
    setFiles(prev => prev.map(file => 
      file.id === id ? { 
          ...file, 
          [field]: value,
          errors: { ...file.errors, [field]: '' } // Clear error on change
      } : file
    ));
  };

  const handleDelete = (id) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  };

  const handleUpload = async (id) => {
    const fileItem = files.find(f => f.id === id);
    if (!fileItem) return;

    const errors = {};
    if (!fileItem.type) errors.type = 'Document Type is required';
    
    if (fileItem.type === 'Agreement') {
        if (!fileItem.activationDate) errors.activationDate = 'Activation Date is required';
        if (!fileItem.expirationDate) errors.expirationDate = 'Expiration Date is required';
    }

    if (Object.keys(errors).length > 0) {
        setFiles(prev => prev.map(f => f.id === id ? { ...f, errors } : f));
        return;
    }

    setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'Uploading...' } : f));

    const formData = new FormData();
    formData.append('file', fileItem.file);
    formData.append('isAgreement', fileItem.type === 'Agreement');
    if (fileItem.activationDate) {
        formData.append('startDate', new Date(fileItem.activationDate).toISOString());
    }
    if (fileItem.expirationDate) {
        formData.append('endDate', new Date(fileItem.expirationDate).toISOString());
    }

    try {
        await axiosClient.patch(`${endpointPrefix}/${siteId}/upload-document`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'Upload Completed' } : f));
        showToast('Document uploaded successfully');
        if (onUploadSuccess) onUploadSuccess();
    } catch (error) {
        setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'Ready to upload' } : f)); // Revert status on error
        showToast(error.message, 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <Modal>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Documents</h3>
          <button className={styles.closeButton} onClick={onClose}>&times;</button>
        </div>
        
        <div className={styles.divider} />

        <div {...getRootProps()} className={`${styles.dropzone} ${isDragActive ? styles.active : ''}`}>
          <input {...getInputProps()} />
          <div className={styles.dropzoneContent}>
             <div className={styles.fileIcon}>
                <Icon name="file-text" />
                <div className={styles.arrowBadge}>
                   <Icon name="arrow-up" />
                </div>
             </div>
             <p className={styles.dropText}>
               Drag and drop your file here or <span className={styles.link}>Choose File</span>
             </p>
             <p className={styles.subText}>
               Maximum File Size : 50 MB<br/>
               Supported Format: PDF
             </p>
          </div>
        </div>

        {files.length > 0 && (
          <div className={styles.fileList}>
            {files.map(fileItem => (
              <div key={fileItem.id} className={styles.fileCard}>
                <div className={styles.mainContent}>
                    <div className={styles.fileInfo}>
                       <div className={styles.iconWrapper}>
                          <Icon name="file-text" />
                       </div>
                       <div className={styles.details}>
                          <span className={styles.fileName}>{fileItem.file.name}</span>
                          <div className={styles.status}>
                             {fileItem.status === 'Upload Completed' ? (
                                <div className={styles.completedIcon}>
                                  <Icon name="check" />
                                </div>
                             ) : (
                                <div className={styles.uploadIcon}>
                                  <Icon name="arrow-up" />
                                </div>
                             )}
                             <span>{fileItem.status}</span>
                          </div>
                       </div>
                    </div>
  
                    <div className={styles.formGrid}>
                       <div className={styles.fieldGroup}>
                          <Dropdown 
                            label="Document Type"
                            options={[
                              { label: 'Agreement', value: 'Agreement' },
                              { label: 'Normal', value: 'Normal' }
                            ]}
                            value={fileItem.type}
                            onChange={(val) => handleUpdateFile(fileItem.id, 'type', val)}
                            disabled={fileItem.status === 'Upload Completed' || fileItem.status === 'Uploading...'}
                            error={fileItem.errors?.type}
                          />
                       </div>
                       
                       
                       {fileItem.type === 'Agreement' && (
                          <>
                              <div className={styles.fieldGroup}>
                                  <CustomDatePicker 
                                  label="Activation Date"
                                  date={fileItem.activationDate}
                                  onChange={(date) => {
                                      handleUpdateFile(fileItem.id, 'activationDate', date);
                                      // If start date > end date, clear end date or handle logically? 
                                      // Requirement says: "if start date is selected set end date min with that. also vice versa"
                                      // Usually just setting min/max on the picker is enough, but if current value is invalid, we might want to clear it.
                                      // For now, just setting constraints.
                                      if (fileItem.expirationDate && date && new Date(fileItem.expirationDate) < date) {
                                         handleUpdateFile(fileItem.id, 'expirationDate', null);
                                      }
                                  }}
                                  showIcon
                                  maxDate={fileItem.expirationDate ? new Date(fileItem.expirationDate) : null}
                                  disabled={fileItem.status === 'Upload Completed' || fileItem.status === 'Uploading...'}
                                  error={fileItem.errors?.activationDate}
                                  />
                              </div>
  
                              <div className={styles.fieldGroup}>
                                  <CustomDatePicker 
                                  label="Expiration Date"
                                  date={fileItem.expirationDate}
                                  onChange={(date) => {
                                      handleUpdateFile(fileItem.id, 'expirationDate', date);
                                      if (fileItem.activationDate && date && new Date(fileItem.activationDate) > date) {
                                         handleUpdateFile(fileItem.id, 'activationDate', null);
                                      }
                                  }}
                                  showIcon
                                  minDate={fileItem.activationDate ? new Date(fileItem.activationDate) : null}
                                  disabled={fileItem.status === 'Upload Completed' || fileItem.status === 'Uploading...'}
                                  error={fileItem.errors?.expirationDate}
                                  />
                              </div>
                          </>
                       )}
                    </div>
                </div>
  
                   <div className={styles.actions}>
                    {fileItem.status !== 'Upload Completed' && (
                       <>
                          <button 
                              className={styles.actionButton} 
                              onClick={() => handleUpload(fileItem.id)}
                              disabled={fileItem.status === 'Uploading...'}
                              title="Upload"
                          >
                          <Icon name="upload" />
                          </button>
                          <button 
                              className={`${styles.actionButton} ${styles.delete}`} 
                              onClick={() => handleDelete(fileItem.id)}
                              disabled={fileItem.status === 'Uploading...'}
                              title="Delete"
                          >
                          <Icon name="trash" />
                          </button>
                       </>
                    )}
                   </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

DocumentUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  siteId: PropTypes.string,
  onUploadSuccess: PropTypes.func,
  endpointPrefix: PropTypes.string
};

export default DocumentUploadModal;
